/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import {
    DateFilterDefault,
    DateFilterOptions,
    DateFilterType,
    FilterOptions,
    FilterOptionsPayload,
    NumberFilterDefault,
    NumberFilterOptions,
    NumberFilterType,
    ReportDefinition,
    ReportFilter,
    ReportFilterMap,
    ReportFilterSubjectMap,
    StringFilterDefault,
    StringFilterOptions,
} from '../../state/report.model';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { concatMapTo, debounceTime, distinctUntilChanged, map, mergeMap, take, takeUntil, tap } from 'rxjs/operators';
import { ReportState } from '../../state/report.state';
import * as moment from 'moment';
import { ClearReportFilters, RefreshFilterFields, SetFilterOptions } from '../../state/report.actions';
import { LoggerService } from '../../../shared/services/logger.service';
import { Actions, ofActionSuccessful, Select, Store } from '@ngxs/store';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { ReportService } from '../../services/report.service';
import { DropDownItem } from '../../state/dataForm.model';
import { DateInterval, NumberRange } from '../../../../types';

@Component({
    selector: 'dash-gis-report-content-filter',
    templateUrl: './report-content-filter.component.html',
    styleUrls: ['./report-content-filter.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportContentFilterComponent implements OnInit, OnChanges, OnDestroy {
    @Input() definition: ReportDefinition;
    @Input() id: string;

    @Output() values = new EventEmitter<any>();

    @Select(ReportState.filters) filters$: Observable<(id: string, reportName: string) => FilterOptions[]>;

    // filter options
    filterGroup: FormGroup;
    filterPanelExpanded = false;

    // Allow all dropdowns to be refreshed
    private optionSubjects: ReportFilterSubjectMap = {};

    /*
     Associated dropdowns are those that change options based on the value of another control.

     The key in these objects is the name of the field that controls the values -- in the
     model, this is the field 'dropdownParameter'

     As an example, 'field1' is a normal dropdown. 'field2' has a parameter value of 'field1'. Therefore,
     this.associatedFields['field1'] would return the name 'field2', and this.associatedOptionSubject['field1']
     would return a subject used to get the options for field2.

     In the method 'onFieldChanges', when field1 is changed, the subject used to update associated dropdown options
     is obtained by accessing this.associatedOptionSubjects with the field name of the changed field (NOT THE
     ASSOCIATED FIELD)
     */
    private associatedFields: ReportFilterMap = {};
    private associatedOptionSubjects: ReportFilterSubjectMap = {};

    private disposeSubject = new Subject<void>();
    private dispose$: Observable<void>;

    private filterDefaultsApplied = false;

    constructor(
        private logger: LoggerService,
        private reportService: ReportService,
        private actions$: Actions,
        private store: Store
    ) {
        this.dispose$ = this.disposeSubject.asObservable();
    }

    ngOnInit() {
        this.subscribeToFilterStateChanges();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.definition?.currentValue) {
            if (!isEqual(changes.definition.currentValue, changes.definition.previousValue)) {
                this.filterDefaultsApplied = false;
                this.filterPanelExpanded = this.definition?.filtersExpanded;

                if (this.definition.filters && this.definition.filters.length > 0) {
                    this.createFilterFormGroup();
                    if (this.filterPanelExpanded) {
                        this.populateFilterDefaults();
                    }
                }
            }
        }
    }

    ngOnDestroy(): void {
        this.disposeSubject.next();
    }

    createFilterFormGroup() {
        const getFiltersFn = this.store.selectSnapshot(ReportState.filters);
        const filters = getFiltersFn(this.id, this.definition.name);
        const controls = {};
        this.definition.filters.forEach((filterDef) => {
            const current = filters.find((f) => f.field === filterDef.field);
            controls[`filter_${filterDef.field}`] = this.createFilterControl(filterDef, current);

            if (filterDef.dropdownItems?.length > 0 || filterDef.dropdownParameter) {
                this.optionSubjects[filterDef.field] = new BehaviorSubject<DropDownItem[]>([]);
                this.subscribeToDataChanges(filterDef);
                this.store.dispatch(new RefreshFilterFields());
            }

            if (filterDef.dropdownParameter) {
                // register field association
                this.associatedFields[filterDef.dropdownParameter] = filterDef;
                this.associatedOptionSubjects[filterDef.dropdownParameter] = new BehaviorSubject<DropDownItem[]>([]);
            }
        });
        this.filterGroup = new FormGroup(controls);

        Object.keys(this.associatedFields).forEach((parentField) => {
            const parentControl = this.filterGroup.get(`filter_${parentField}`);
            if (parentControl) {
                parentControl.valueChanges
                    .pipe(
                        distinctUntilChanged(isEqual),
                        debounceTime(100),
                        tap(() => {
                            const childFilter = this.associatedFields[parentField];
                            const childControl = this.filterGroup.get(`filter_${childFilter.field}`);
                            childControl.patchValue(null);
                        })
                    )
                    .subscribe();
            }
        });

        this.subscribeToFilterFormChanges();
    }

    populateFilterDefaults() {
        if (this.filterDefaultsApplied) {
            return;
        }
        this.filterDefaultsApplied = true;

        this.definition.filters.forEach((f) => {
            if (f.default && !f.hidden) {
                const field = this.getFieldDefinition(f.field);

                if (!field) {
                    this.logger.error(`Could not find field with name ${f.field}`);
                    return;
                }

                const filterOption = this.getDefaultOption(f);
                if (filterOption) {
                    this.updateFilterValue(filterOption);
                }
            }
        });
        this.filterGroup.updateValueAndValidity();
    }

    subscribeToFilterStateChanges() {
        this.filters$
            .pipe(
                map((fn) => fn(this.id, this.definition.name)),
                distinctUntilChanged(isEqual),
                takeUntil(this.dispose$)
            )
            .subscribe((filters: FilterOptions[]) => {
                filters.forEach((f) => {
                    this.updateFilterValue(f);
                });
            });
    }

    subscribeToFilterFormChanges() {
        this.filterGroup.valueChanges
            .pipe(
                distinctUntilChanged(isEqual),
                debounceTime(100),
                map((value) => {
                    const filterPayload: FilterOptionsPayload = {
                        id: this.id,
                        reportName: this.definition.name,
                        filterOptions: [],
                    };
                    this.definition.filters.forEach((filterDef) => {
                        let controlName;
                        let minControlName;
                        let maxControlName;
                        let controlValue: FilterOptions;
                        switch (filterDef.type) {
                            case 'string':
                            case 'string-exact':
                                const stringControlValue: Partial<StringFilterOptions> = {
                                    type: filterDef.type,
                                    field: filterDef.field,
                                };
                                controlName = `filter_${filterDef.field}`;
                                stringControlValue.value = value[controlName];
                                controlValue = stringControlValue as StringFilterOptions;
                                this.subscribeToAssociatedField(filterDef, controlValue);
                                break;
                            case 'number':
                                const numberControlValue: Partial<NumberFilterOptions> = {
                                    type: filterDef.type,
                                    field: filterDef.field,
                                };
                                controlName = `filter_${filterDef.field}`;
                                if (value[controlName]?.min) {
                                    numberControlValue.min = value[controlName]?.min;
                                }
                                if (value[controlName]?.max) {
                                    numberControlValue.max = value[controlName]?.max;
                                }
                                controlValue = numberControlValue as NumberFilterOptions;
                                break;
                            case 'date':
                                const dateControlValue: Partial<DateFilterOptions> = {
                                    type: filterDef.type,
                                    field: filterDef.field,
                                };
                                controlName = `filter_${filterDef.field}`;
                                if (value[controlName]?.min) {
                                    dateControlValue.min = value[controlName].min;
                                }
                                if (value[controlName]?.max) {
                                    dateControlValue.max = value[controlName].max;
                                }
                                controlValue = dateControlValue as DateFilterOptions;
                                break;
                        }
                        filterPayload.filterOptions.push(controlValue as FilterOptions);
                    });
                    return filterPayload;
                }),
                mergeMap((filterPayload) => this.store.dispatch(new SetFilterOptions(filterPayload))),
                takeUntil(this.dispose$)
            )
            .subscribe();
    }

    getFilterFieldDisplayName(fieldName: string) {
        const field = this.getFieldDefinition(fieldName);
        return field ? field.display : '';
    }

    getFilterValue(fieldName: string, prop: 'min' | 'max' | 'value') {
        return this.filterGroup.get(`filter_${fieldName}`).value;
    }

    getFieldDefinition(name: string) {
        const fieldIndex = this.definition.fields.findIndex((f) => f.name === name);
        return fieldIndex !== -1 ? this.definition.fields[fieldIndex] : false;
    }

    filterTrackByFn(index: number, item: ReportFilter) {
        return `${item.field}_${item.type}`;
    }

    getDropdownOptions$(filter: ReportFilter): Observable<any[]> {
        if (filter.dropdownParameter) {
            const associatedSubject = this.associatedOptionSubjects[filter.dropdownParameter];
            return associatedSubject ? associatedSubject.asObservable() : of([]);
        }
        const subject = this.optionSubjects[filter.field];
        return subject ?? of([]);
    }

    shouldDisplaySelect(filter: ReportFilter): boolean {
        return filter.dropdownItems?.length > 0 || !!filter.dropdownParameter;
    }

    clearFilters() {
        this.store
            .dispatch(
                new ClearReportFilters({
                    id: this.id,
                    reportName: this.definition.name,
                })
            )
            .pipe(take(1))
            .subscribe(() => this.filterGroup.updateValueAndValidity());
    }

    get filterGroupEnabled() {
        let enabled = false;
        if (this.definition?.filters?.length > 0 && Object.keys(this.filterGroup?.controls || {}).length > 0) {
            enabled = this.definition.filters.reduce((p, v) => p || !v.hidden, enabled);
        }

        return enabled;
    }

    get filters() {
        return this.definition?.filters?.filter((f) => !f.hidden) || [];
    }

    private getDefaultOption(filterDefinition: ReportFilter): FilterOptions {
        switch (filterDefinition.type) {
            case 'string':
            case 'string-exact':
                return this.getDefaultStringOption(filterDefinition);
            case 'number':
                return this.getDefaultNumberOption(filterDefinition);
            case 'date':
                return this.getDefaultDateOption(filterDefinition);
        }
    }

    private getDefaultStringOption(filterDefinition: ReportFilter): FilterOptions {
        const stringDefault = filterDefinition.default as StringFilterDefault;
        return {
            field: filterDefinition.field,
            type: filterDefinition.type,
            value: stringDefault.value,
        };
    }

    private getDefaultNumberOption(filterDefinition: ReportFilter) {
        const numberDefault = filterDefinition.default as NumberFilterDefault;
        const numberOption: NumberFilterOptions = {
            field: filterDefinition.field,
            type: filterDefinition.type as NumberFilterType,
        };
        if (numberDefault.min) {
            numberOption.min = numberDefault.min;
        }
        if (numberDefault.max) {
            numberOption.max = numberDefault.max;
        }
        return numberOption;
    }

    private getDefaultDateOption(filterDefinition: ReportFilter) {
        const dateDefault = filterDefinition.default as DateFilterDefault;
        const dateOption: DateFilterOptions = {
            field: filterDefinition.field,
            type: filterDefinition.type as DateFilterType,
        };
        if (dateDefault.min) {
            let minValue;
            if (dateDefault.min === 'current') {
                minValue = moment().toDate();
            } else {
                minValue = moment(dateDefault.min).toDate();
            }
            dateOption.min = minValue;
        }
        if (dateDefault.max) {
            let maxValue;
            if (dateDefault.max === 'current') {
                maxValue = moment().toDate();
            } else {
                maxValue = moment(dateDefault.max).toDate();
            }
            dateOption.max = maxValue;
        }
        return dateOption;
    }

    private updateFilterValue(filterDefinition: FilterOptions) {
        switch (filterDefinition.type) {
            case 'string':
            case 'string-exact':
                this.updateFilterStringValue(filterDefinition);
                break;
            case 'number':
                this.updateFilterNumberValue(filterDefinition);
                break;
            case 'date':
                this.updateFilterDateValue(filterDefinition);
                break;
        }
    }

    private updateFilterStringValue(filterDefinition: StringFilterOptions) {
        if (filterDefinition.value) {
            const filterControl = this.filterGroup.get(`filter_${filterDefinition.field}`);
            if (filterControl) {
                filterControl.patchValue(filterDefinition.value, { emitEvent: false });
            }
        }
    }

    private updateFilterNumberValue(filterDefinition: NumberFilterOptions) {
        const value = {};
        if (filterDefinition.min) {
            value['min'] = filterDefinition.min;
        }
        if (filterDefinition.max) {
            value['max'] = filterDefinition.max;
        }
        const filterControl = this.filterGroup.get(`filter_${filterDefinition.field}`);
        filterControl?.patchValue(value, { emitEvent: false });
    }

    private updateFilterDateValue(filterDefinition: DateFilterOptions) {
        const value = {};
        if (filterDefinition.min) {
            if (Number.isNaN(Date.parse(filterDefinition.min))) {
                return;
            }
            value['min'] = filterDefinition.min;
        }
        if (filterDefinition.max) {
            if (Number.isNaN(Date.parse(filterDefinition.max))) {
                return;
            }
            value['max'] = filterDefinition.max;
        }
        const control = this.filterGroup.get(`filter_${filterDefinition.field}`);
        control?.patchValue(value, { emitEvent: false });
    }

    private subscribeToDataChanges(filterDef: ReportFilter) {
        const subject = this.optionSubjects[filterDef.field];
        this.actions$
            .pipe(
                ofActionSuccessful(RefreshFilterFields),
                concatMapTo(
                    this.reportService.getDropDownOptions$(
                        this.definition.name,
                        this.definition.type,
                        filterDef.field,
                        filterDef.type,
                        null
                    )
                ),
                takeUntil(this.dispose$)
            )
            .subscribe((options: DropDownItem[]) => subject.next(options));
    }

    private subscribeToAssociatedField(filterDef: ReportFilter, controlValue: StringFilterOptions) {
        // check associated fields
        const associatedField = this.associatedFields[filterDef.field];
        if (associatedField) {
            const associatedControlName = `filter_${associatedField.field}`;
            const associatedControl = this.filterGroup.get(associatedControlName);
            const subject = this.associatedOptionSubjects[filterDef.field];
            if (controlValue.value) {
                this.reportService
                    .getDropDownOptions$(
                        this.definition.name,
                        this.definition.type,
                        associatedField.field,
                        associatedField.type,
                        controlValue.value
                    )
                    .pipe(takeUntil(this.dispose$))
                    .subscribe((options) => subject.next(options));

                associatedControl.enable({ onlySelf: true });
            } else {
                subject.next([]);
                associatedControl.patchValue(null);
                associatedControl.disable({ onlySelf: true });
            }
        }
    }

    private createFilterControl(filterDefinition: ReportFilter, current: FilterOptions): AbstractControl {
        let control: FormControl;
        let currentValue: FilterOptions;
        const controlOptions = {
            value: null,
            disabled: false,
        };
        if (filterDefinition.dropdownItems?.length > 0 && filterDefinition.dropdownParameter) {
            controlOptions['disabled'] = true;
        }

        switch (filterDefinition.type) {
            case 'string':
            case 'string-exact':
                currentValue = current as StringFilterOptions;
                if (currentValue?.value) {
                    controlOptions['value'] = currentValue.value;
                }
                control = new FormControl<string>(controlOptions);
                break;
            case 'number':
                currentValue = current as NumberFilterOptions;
                if (currentValue) {
                    controlOptions['value'] = currentValue;
                }
                control = new FormControl<NumberRange>(controlOptions);
                break;
            case 'date':
                currentValue = current as DateFilterOptions;
                const controlValue = {};
                if (currentValue?.min) {
                    controlValue['min'] = currentValue.min;
                }
                if (currentValue?.max) {
                    controlValue['max'] = currentValue.max;
                }
                control = new FormControl<DateInterval>(controlOptions);
                break;
        }
        return control;
    }
}
